import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ClearIcon from '@mui/icons-material/Clear';
import QuestionMarkIcon from '@mui/icons-material/HelpTwoTone';
import MenuIcon from '@mui/icons-material/Menu';
import { Autocomplete, Avatar, Box, Chip, CircularProgress, IconButton, InputAdornment, Menu, MenuItem, Stack, TextField, Typography, debounce } from '@mui/material';
import React from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { exportBookmarks } from '../ApiClient';
import { removeExistingParam, replaceExistingParamsValue } from '../SearchParamsUtils';
import { useFeedbackContext } from '../context/FeedbackContext';
import { useUIContext } from '../context/UIContext';
import { useDataContext } from '../context/DataContext';
import { UserState, useUserContext } from '../context/UserContext';
import { AddBookmarkDialog } from './AddBookmarkDialog';
import { BackupApiKeyDialog } from './BackupApiKeyDialog';

export const Header: React.FC = () => {

  // hooks
  const { user, userState, logout } = useUserContext();
  const { showMessage, showErrorMessage } = useFeedbackContext();
  const { allTags } = useDataContext();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { loadingBookmarks, targetSearch, targetTags } = useUIContext();
  const { setTargetFromUrlParams, saving } = useUIContext();

  // UI state
  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [userMenuAnchor, setUserMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [searchTextContent, setSearchTextContent] = React.useState<string>('');
  const [selectedTags, setSelectedTags] = React.useState<string[]>([]);
  const [showAddBookmarkDialog, setShowAddBookmarkDialog] = React.useState(false);
  const [showBackupApiKeyDialog, setShowBackupApiKeyDialog] = React.useState(false);
  const latestSearchParamsRef = React.useRef(searchParams);

  /*
 when the URL search params change - usually because the user has changed the
 header search box or the limit select - update the context, that will alter the
 target state and trigger the effect that will fetch the bookmarks below
 */
  React.useEffect(() => {
    setTargetFromUrlParams(searchParams);
    latestSearchParamsRef.current = searchParams;
  }, [searchParams, setTargetFromUrlParams]);

  /*
  If the user enters/clicks a URL directly or uses the browser back/forward buttons,
  the URL search params will change and this effect will trigger to keep
  the UI state in sync with the URL search params
  */
  React.useEffect(() => {
    setSearchTextContent(targetSearch || '');
  }, [targetSearch]);

  // Sync selected tags from URL params
  React.useEffect(() => {
    setSelectedTags(targetTags);
  }, [targetTags]);

  const handleExport = async (format: string) => {
    if (user && userState === UserState.LOGGED_IN) {
      showMessage('Exporting bookmarks...');
      const element: HTMLAnchorElement = document.createElement('a');
      const csv = await exportBookmarks(user, format);
      if (!csv) {
        showErrorMessage('Error exporting bookmarks');
        return;
      }
      else {
        const file: Blob = new Blob([csv], { type: 'application/csv' });
        element.href = URL.createObjectURL(file);
        element.download = `stashsite-export-${new Date().toISOString()}.${format}`;
        document.body.appendChild(element);
        element.click();
        showMessage('CSV exported');
      }
    }
  };

  const debounceSetSearchParams = React.useMemo(() => debounce((value: string) => {
    setSearchParams(replaceExistingParamsValue(latestSearchParamsRef.current, 'search', value));
  }, 500), []); // 500 ms debounce time

  React.useEffect(() => {
    return () => {
      debounceSetSearchParams.clear();
    };
  }, [debounceSetSearchParams]);


  return (
    <Box
      id='stashsite-header'
      sx={{
        borderBottom: '1px solid',
        borderColor: 'divider',
        pb: 1, pt: 1,
        position: 'sticky',
        top: 0, left: 0, backgroundColor: 'background.default', zIndex: 2
      }}
    >
      <Box >
        <Stack direction='row' alignItems='center'>
          <IconButton
            aria-label="more" size="small"
            onClick={(event) => setMenuAnchor(event.currentTarget)}
            disabled={!user}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant='h1' align='center'
            fontWeight={700} fontSize='24px'
            px={1}
          >stashsite</Typography>

          <IconButton
            onClick={() => setShowAddBookmarkDialog(true)}
            sx={{ fontSize: '24px' }}
            aria-label='add bookmark'
            color='secondary'
            disabled={userState !== UserState.LOGGED_IN || saving || loadingBookmarks}
          >
            <AddCircleOutlineIcon />
          </IconButton>
          <div style={{ flexGrow: 1 }}></div>

          <CircularProgress sx={{ display: (saving || loadingBookmarks) ? 'block' : 'none' }} size={20} color='secondary' />
          <IconButton
            onClick={(event) => setUserMenuAnchor(event.currentTarget)}
            size="small"
          >
            <Avatar
              sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}
              src={user?.picture}
            >
              {!user ? (
                <QuestionMarkIcon sx={{ color: 'white' }} />
              ) : user.picture ? null : (
                <Typography variant="body2">
                  {user.email.charAt(0).toUpperCase()}
                </Typography>
              )}
            </Avatar>
          </IconButton>

        </Stack>

      </Box>

      <Stack direction='row' sx={{ mt: 1 }}>
        <Autocomplete
          multiple
          freeSolo
          fullWidth
          disableClearable
          options={allTags.filter(t => !selectedTags.includes(t))}
          value={selectedTags}
          inputValue={searchTextContent}
          onInputChange={(_event, newInputValue, reason) => {
            if (reason === 'input') {
              setSearchTextContent(newInputValue);
              debounceSetSearchParams(newInputValue);
            }
          }}
          onChange={(_event, newValue, reason) => {
            if (reason !== 'selectOption' && reason !== 'removeOption' && reason !== 'clear') {
              return;
            }
            // newValue is the array of selected tags (strings)
            const tags = newValue.filter(v => typeof v === 'string') as string[];
            setSelectedTags(tags);
            setSearchTextContent('');
            debounceSetSearchParams.clear();
            // Update tags URL param
            let newParams = replaceExistingParamsValue(searchParams, 'tags', tags.join(','));
            delete newParams.search;
            setSearchParams(newParams);
          }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option}
                label={option}
                size="small"
                color="secondary"
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              id='search' variant='outlined' size='small'
              placeholder={selectedTags.length > 0 ? '' : 'Search bookmarks...'}
              InputProps={{
                ...params.InputProps,
                sx: { backgroundColor: 'white' },
                endAdornment: (
                  <>
                    <InputAdornment position="end">
                      <IconButton onClick={() => {
                        setSearchTextContent('');
                        setSelectedTags([]);
                        let newParams = removeExistingParam(searchParams, 'search');
                        newParams = { ...newParams };
                        delete newParams['tags'];
                        setSearchParams(newParams);
                      }}>
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  </>
                ),
              }}
            />
          )}
        />
      </Stack>
      <Menu
        anchorEl={userMenuAnchor}
        open={!!userMenuAnchor}
        onClose={() => setUserMenuAnchor(null)}
      >
        {!user ? (
          <MenuItem onClick={() => {
            setUserMenuAnchor(null);
            navigate('/login');
          }}>
            Log in
          </MenuItem>
        ) : (
          <MenuItem onClick={() => {
            setUserMenuAnchor(null);
            logout();
          }}>
            Log out {user.email}
          </MenuItem>
        )}
      </Menu>
      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={() => {
          setMenuAnchor(null);
          navigate('/');
        }}>Home</MenuItem>
        <MenuItem onClick={() => {
          setMenuAnchor(null);
          navigate('/share');
        }}>Queue</MenuItem>
        <MenuItem onClick={() => {
          setMenuAnchor(null);
          handleExport('json');
        }}>Download JSON backup</MenuItem>
        <MenuItem onClick={() => {
          setMenuAnchor(null);
          handleExport('csv');
        }}>Download CSV backup</MenuItem>
        <MenuItem onClick={() => {
          setMenuAnchor(null);
          navigate('/pocket');
        }}>Import from Pocket</MenuItem>
        <MenuItem onClick={() => {
          setMenuAnchor(null);
          setShowBackupApiKeyDialog(true);
        }}>Create backup URL and key</MenuItem>
        <MenuItem onClick={() => {
          setMenuAnchor(null);
          navigate('/about');
        }}>About StashSite</MenuItem>
      </Menu>
      <AddBookmarkDialog
        open={showAddBookmarkDialog}
        close={() => setShowAddBookmarkDialog(false)}
      />
      <BackupApiKeyDialog
        open={showBackupApiKeyDialog}
        close={() => setShowBackupApiKeyDialog(false)}
      />

    </Box>
  );
};
